import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  X as CloseIcon,
  Search as SearchIcon,
  ArrowLeft as ArrowLeftIcon,
  Loader,
} from 'lucide-react';
import AnswerDisplay from './components/AnswerDisplay';
import SearchResults from './components/SearchResults';
import {
  useSearchInput,
  useSearchPopover,
  useCitationsAndThumbnails,
  useCommandSuggestions,
} from './hooks';
import useBackendAssistantSearch from './hooks/useBackendAssistantSearch';
import useLimits from '../plan/hooks/limits';
import useUserTags from '../../hooks/useTags';
import assistantActions from './actions';
import { pushModal } from '../globalModals/globalModalsSlice';
import { Command } from './commands';
import { initializeRecorder } from '../recording/recordingSlice';
import { getFolderUrl } from '../../lib/routingUtils';
import { setDrawerState } from '../dashboard/dashboardSlice';
import { ContactSupportDialog } from '../support';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { RootState } from '../../reducers';

interface AssistantToolbarProps {
  onFocusChange?: (isFocused: boolean) => void;
}

export const AssistantToolbar: React.FC<AssistantToolbarProps> = ({
  onFocusChange,
}) => {
  const { t } = useTranslation();
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);

  const {
    status,
    results,
    error,
    isGeneratingAnswer,
    performSearch,
    reset: resetSearch,
  } = useBackendAssistantSearch();

  const location = useLocation();
  const navigate = useNavigate();
  const guardLimits = useLimits();
  const { setTag } = useUserTags();
  const dispatch = useDispatch();
  const isMobileViewport = window.innerWidth < 640;

  // Get subscription plan from Redux state
  const plan = useSelector((state: RootState) => state.plan);
  const subscriptionPlan = plan?.subscription?.package || 'FREE_LOGGEDIN';

  const {
    activeSource,
    setActiveSource,
    fileThumbnails,
    setFileThumbnails,
    hideReferences,
    resetCitationCount,
    showVideoPlayer,
    videoPlayerFile,
    handleSourceClick: handleSourceClickFromHook,
  } = useCitationsAndThumbnails();

  useEffect(() => {
    if (results?.searchResults) {
      const newThumbnails = results.searchResults.map((file) => ({
        fileId: file._id,
        thumbnailUrl: file.thumbUrl || null,
        isLoading: false,
      }));
      setFileThumbnails(newThumbnails);
    }
  }, [results?.searchResults, setFileThumbnails]);

  const [citationThumbnails, setCitationThumbnails] = useState<
    Array<{
      fileId: string;
      timestamp: string;
      thumbnailUrl: string | null;
      isLoading: boolean;
    }>
  >([]);

  const fetchCitationThumbnail = useCallback(
    (fileId: string, timestamp: string) => {
      const existing = citationThumbnails.find(
        (thumbnail) =>
          thumbnail.fileId === fileId && thumbnail.timestamp === timestamp,
      );
      if (existing) return;

      setCitationThumbnails((prev) => [
        ...prev,
        {
          fileId,
          timestamp,
          thumbnailUrl: null,
          isLoading: true,
        },
      ]);

      setTimeout(() => {
        const file = results?.searchResults?.find((f) => f._id === fileId);
        setCitationThumbnails((prev) =>
          prev.map((thumbnail) =>
            thumbnail.fileId === fileId && thumbnail.timestamp === timestamp
              ? {
                  ...thumbnail,
                  thumbnailUrl: file?.thumbUrl || null,
                  isLoading: false,
                }
              : thumbnail,
          ),
        );
      }, 100);
    },
    [citationThumbnails, results?.searchResults],
  );

  const formatTimestamp = useCallback((seconds: string) => {
    const totalSeconds = parseFloat(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = Math.floor(totalSeconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const {
    query,
    setQuery,
    inputRef,
    handleInputChange: handleRawInputChange,
    handleKeyDown: handleRawKeyDown,
    handleClear: handleRawClear,
    updateQuery,
  } = useSearchInput();

  const {
    isPopoverOpen,
    setIsPopoverOpen,
    containerRef,
    searchBarWidth,
    isFileListExpanded,
    isSearchIconMode,
    isSearchExpanded,
    setIsSearchExpanded,
    toggleFileList,
    getShortcutText,
    screenWidth,
  } = useSearchPopover(results?.aiAnswer || null, {
    data: results?.searchResults || [],
    error,
  });

  const { suggestions } = useCommandSuggestions(query);

  const [maxVisibleCards, setMaxVisibleCards] = useState<number>(4);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);

  const getPlaceholderText = () => {
    return t('assistant.placeholder');
  };

  const isURL = (str: string) => {
    const urlPattern =
      /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return urlPattern.test(str);
  };

  const handleOpenImportModal = (url: string) => {
    dispatch(
      assistantActions.trackAssistantQuery({ query: `URL import: ${url}` }),
    );
    dispatch(
      pushModal({
        type: 'import-urls-modal',
        url,
      }),
    );
  };

  const handleSearch = useCallback(async () => {
    if (query.trim()) {
      if (isURL(query.trim())) {
        handleOpenImportModal(query.trim());
        return;
      }

      dispatch(assistantActions.trackAssistantQuery({ query: query.trim() }));

      guardLimits('assistant', async () => {
        setIsPopoverOpen(true);
        resetCitationCount();
        setActiveSource(null);

        updateQuery({ searchQuery: query.trim() });

        await performSearch(query.trim());

        setIsPopoverOpen(true);
      });
    }
  }, [
    query,
    dispatch,
    guardLimits,
    setIsPopoverOpen,
    resetCitationCount,
    setActiveSource,
    updateQuery,
    performSearch,
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleRawInputChange(e);
    setHasUserInteracted(true); // Mark that user has interacted with input

    const newQuery = e.target.value;
    if (newQuery.length > 0 && status === 'succeeded') {
      resetSearch();
    }

    if (
      newQuery.length === 0 &&
      (results?.aiAnswer ||
        (results?.searchResults && results.searchResults.length > 0))
    ) {
      setIsPopoverOpen(true);
    } else if (
      newQuery.length === 0 &&
      !results?.aiAnswer &&
      (!results?.searchResults || results.searchResults.length === 0)
    ) {
      setIsPopoverOpen(false);
    }

    if (newQuery.length > 0) {
      setIsPopoverOpen(true);
    }
  };

  // Auto-trigger AI search after 3 seconds for paid users
  useEffect(() => {
    // Check if user is paid
    const isPaidUser =
      subscriptionPlan !== 'FREE' && subscriptionPlan !== 'FREE_LOGGEDIN';

    if (!isPaidUser || !query || query.length < 3 || !hasUserInteracted) {
      return undefined;
    }

    // Don't auto-trigger if already generating answer or already have an AI answer
    if (isGeneratingAnswer || results?.aiAnswer) {
      return undefined;
    }

    // Set up 3-second delay
    const autoTriggerTimeout = setTimeout(() => {
      // Double-check conditions before triggering
      if (
        query &&
        query.length >= 3 &&
        !isGeneratingAnswer &&
        !results?.aiAnswer
      ) {
        console.log('Auto-triggering AI search for query:', query);
        handleSearch();
      }
    }, 3000);

    // Cleanup timeout on query change or unmount
    return () => {
      clearTimeout(autoTriggerTimeout);
    };
  }, [
    query,
    subscriptionPlan,
    isGeneratingAnswer,
    results?.aiAnswer,
    handleSearch,
    hasUserInteracted,
  ]);

  const isFocused =
    isPopoverOpen ||
    query.length > 0 ||
    (document.activeElement === inputRef.current && screenWidth < 600);

  const handleCommandAction = (command: Command) => {
    dispatch(
      assistantActions.trackAssistantQuery({
        query: `${command.key}: ${command.command}`,
      }),
    );

    dispatch(
      assistantActions.trackAssistantAction({
        actionType: command.action,
        actionName: command.key,
        query: query.trim() || `${command.key}: ${command.command}`,
      }),
    );

    const { pathname } = location;
    const pathSegments = pathname.split('/').filter(Boolean);
    const isLibraryContext =
      pathSegments[0] === 'library' && pathSegments.length >= 3;
    const contextTeamId = isLibraryContext ? pathSegments[1] : null;
    const contextFolderId = isLibraryContext ? pathSegments[2] : null;

    setIsPopoverOpen(false);

    onFocusChange?.(false);

    switch (command.action) {
      case 'import-from-url':
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          }),
        );
        break;
      case 'record-audio':
        if (isLibraryContext && contextTeamId && contextFolderId) {
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: false, webCam: false },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            }),
          );
        } else {
          navigate('/library');
        }
        break;
      case 'navigate-to-folder':
        if (isLibraryContext && contextTeamId && command.folderId) {
          navigate(
            getFolderUrl({
              teamId: contextTeamId,
              folderId: command.folderId,
              withBasePath: false,
            }),
          );

          if (isMobileViewport) {
            dispatch(setDrawerState('closed'));
          }
        } else {
          navigate('/library');
        }
        break;
      case 'record-screen':
        if (
          command.key === 'instant-meeting' ||
          command.key === 'media-record-meeting'
        ) {
          navigate('/tool/p2pmeeting');
        } else if (isLibraryContext && contextTeamId && contextFolderId) {
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: true, webCam: true },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            }),
          );
        } else {
          navigate('/library');
        }
        break;
      case 'record-video':
        if (isLibraryContext && contextTeamId && contextFolderId) {
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: false, webCam: true },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            }),
          );
        } else {
          navigate('/library');
        }
        break;
      case 'upload':
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          }),
        );
        break;
      case 'join-meeting':
        navigate('/settings/meetingbot');
        break;
      case 'calendar-connect':
        navigate('/settings/meetingbot');
        break;
      case 'add-team-member':
        dispatch(
          pushModal({
            type: 'invite-by-link-modal',
          }),
        );
        break;
      case 'integrations':
        navigate('/settings/integrations');
        break;
      case 'mute-settings':
        navigate('/settings/recorder');
        break;
      case 'rating-settings':
        navigate('/settings/user');
        break;
      case 'recording-history':
        navigate('/library');
        break;
      case 'user-settings':
        navigate('/settings/user');
        break;
      case 'team-settings':
        navigate('/settings/team');
        break;
      case 'display-settings':
        navigate('/settings/display');
        break;
      case 'ai-settings':
        navigate('/settings/ai');
        break;
      case 'usage-settings':
        navigate('/settings/usage');
        break;
      case 'recorder-settings':
        navigate('/settings/recorder');
        break;
      case 'billing-settings':
        navigate('/settings/billing');
        break;
      case 'plugin-settings':
        navigate('/settings/plugin');
        break;
      case 'integrations-settings':
        navigate('/settings/integrations');
        break;
      case 'meetingbot-settings':
        navigate('/settings/meetingbot');
        break;
      case 'recovery-settings':
        navigate('/settings/recovery');
        break;
      case 'contact-support':
        setSupportDialogOpen(true);
        break;
      default:
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          }),
        );
        break;
    }

    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim() && isURL(query.trim())) {
      e.preventDefault();
      handleOpenImportModal(query.trim());
      return;
    }

    if (e.key === 'Enter' && suggestions.length > 0 && isPopoverOpen) {
      e.preventDefault();
      handleCommandAction(suggestions[0]);
      return;
    }

    handleRawKeyDown(e, handleSearch, () => setIsPopoverOpen(false));
  };

  const isLoading = status === 'loading';
  const hasContent =
    (results?.searchResults && results.searchResults.length > 0) ||
    results?.aiAnswer ||
    status === 'failed';
  const shouldShowContent = hasContent || isLoading;

  const hasPopoverContentToShow = shouldShowContent || suggestions.length > 0;

  const shouldShowPopover = isPopoverOpen && hasPopoverContentToShow;

  const shouldRoundBottomCorners = !hasPopoverContentToShow || !isPopoverOpen;

  const handleInputFocus = () => {
    setHasUserInteracted(true); // Mark that user has interacted with input

    if (inputRef.current) {
      inputRef.current.focus();
    }

    if (hasPopoverContentToShow) {
      setIsPopoverOpen(true);
    } else {
      setIsPopoverOpen(false);
    }

    if (isSearchIconMode) {
      setIsSearchExpanded(true);
    }

    if (screenWidth < 600 && hasPopoverContentToShow) {
      setIsPopoverOpen(true);
    }

    onFocusChange?.(true);
  };

  const handlePopoverMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
    onFocusChange?.(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    handleRawClear(e, () => {
      handleRawInputChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>);

      if (screenWidth < 600) {
        setIsPopoverOpen(true);
        if (inputRef.current) {
          inputRef.current.focus();
        }
        return;
      }

      if (!hasContent) {
        setIsPopoverOpen(false);
        onFocusChange?.(false);
      }
    });
  };

  useEffect(() => {
    const handleFocusChange = () => {
      if (screenWidth < 600 && document.activeElement === inputRef.current) {
        setIsPopoverOpen(true);
      }
    };

    if (inputRef.current) {
      inputRef.current.addEventListener('focus', handleFocusChange);
    }

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('focus', handleFocusChange);
      }
    };
  }, [screenWidth, setIsPopoverOpen]);

  const handleBackButtonPress = (
    e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setIsPopoverOpen(false);

    handleRawInputChange({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);

    onFocusChange?.(false);
  };

  const handleClearHistory = useCallback(() => {
    setTag({ key: 'assistant_last_answer', value: '' });
    setTag({ key: 'assistant_last_results', value: '' });

    resetSearch();

    setIsPopoverOpen(false);

    if (inputRef.current) {
      inputRef.current.value = '';
      handleRawInputChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [setTag, resetSearch, setIsPopoverOpen, handleRawInputChange]);

  const handleSearchIconClick = useCallback(() => {
    if (isSearchIconMode && !isSearchExpanded) {
      setIsSearchExpanded(true);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [isSearchIconMode, isSearchExpanded, setIsSearchExpanded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isSearchIconMode &&
        isSearchExpanded &&
        !query.trim()
      ) {
        setIsSearchExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchIconMode, isSearchExpanded, query, setIsSearchExpanded]);

  useEffect(() => {
    if (results?.searchResults && results.searchResults.length > 0) {
      const firstFile = results.searchResults[0];
      if (firstFile?._id) {
        setActiveSource({ fileId: firstFile._id, timestamp: '0' });
      }
    }
  }, [results?.searchResults]);

  const handleSourceClick = useCallback(
    (fileId: string, timestamp: string) => {
      // Use the hook's handleSourceClick which properly manages video player state
      if (results?.searchResults) {
        handleSourceClickFromHook(fileId, timestamp, results.searchResults);
      }
    },
    [results?.searchResults, handleSourceClickFromHook],
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={cn(
        'relative w-full min-w-[100px] sm:min-w-[300px] max-w-full mx-0 ml-0 flex-1 flex',
        isFocused ? 'z-[2200] mr-0' : 'z-[2150] mr-[2px] sm:mr-0',
      )}
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
    >
      {isSearchIconMode && !isSearchExpanded ? (
        <Button
          variant="ghost"
          size="icon"
          className="w-10 h-10 rounded-full bg-white dark:bg-background hover:bg-black/5"
          onClick={handleSearchIconClick}
        >
          <SearchIcon className="w-5 h-5" />
        </Button>
      ) : (
        <div className="relative w-full">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center z-10">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              className={cn(
                'w-[18px] h-[18px] flex items-center justify-center relative overflow-hidden',
                isFocused || isPopoverOpen
                  ? 'cursor-pointer'
                  : 'cursor-default',
              )}
              onClick={
                isFocused || isPopoverOpen ? handleBackButtonPress : undefined
              }
            >
              <div
                className={cn(
                  'absolute flex items-center justify-center w-full h-full text-gray-500',
                  isFocused || isPopoverOpen
                    ? 'opacity-100 scale-100 visible'
                    : 'opacity-0 scale-80 invisible',
                )}
              >
                <ArrowLeftIcon className="w-[18px] h-[18px]" />
              </div>
              <div
                className={cn(
                  'absolute flex items-center justify-center w-full h-full text-gray-500',
                  isFocused || isPopoverOpen
                    ? 'opacity-0 scale-80 invisible'
                    : 'opacity-100 scale-100 visible',
                )}
              >
                <SearchIcon className="w-[18px] h-[18px]" />
              </div>
            </div>
          </div>

          <Input
            placeholder={getPlaceholderText()}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            ref={inputRef}
            className={cn(
              'w-full h-10 pl-10 pr-12 bg-white dark:bg-secondary border-0 shadow-sm',
              shouldRoundBottomCorners
                ? 'rounded-[20px]'
                : 'rounded-t-[20px] rounded-b-none',
              'focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0',
              'hover:opacity-90 focus:opacity-95',
            )}
            onClick={(e) => {
              e.stopPropagation();
              handleInputFocus();
            }}
          />

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {!query && !isPopoverOpen && (
              <span className="text-xs text-black/40 dark:text-white/60 mr-1 select-none">
                {getShortcutText()}
              </span>
            )}
            {query && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClear}
                className={cn(
                  'w-[22px] h-[22px] p-0 rounded-full',
                  isLoading
                    ? 'cursor-default'
                    : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-110 active:scale-95',
                )}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader className="w-[18px] h-[18px] animate-spin" />
                ) : (
                  <CloseIcon className="w-[18px] h-[18px]" />
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {shouldShowPopover && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={cn(
            'absolute top-full left-0 right-0 z-[2200] max-h-[70vh] overflow-auto',
            'rounded-b-[20px] bg-white dark:bg-secondary shadow-sm border-0 -mt-px',
          )}
          style={{
            width: searchBarWidth ? `${searchBarWidth}px` : undefined,
          }}
          onMouseDown={handlePopoverMouseDown}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div className="p-3" onMouseDown={handlePopoverMouseDown}>
            {suggestions.length > 0 && (
              <div
                className={cn(
                  'flex gap-2 flex-nowrap p-2 overflow-x-hidden',
                  shouldShowContent ? 'mb-2 rounded-lg' : 'rounded-b-lg',
                )}
                ref={(el: HTMLDivElement | null) => {
                  if (el) {
                    const containerWidth = el.offsetWidth;
                    const effectiveCardWidth = 160;
                    const calculatedMaxCards = Math.max(
                      2,
                      Math.floor(containerWidth / effectiveCardWidth),
                    );
                    if (calculatedMaxCards !== maxVisibleCards) {
                      setMaxVisibleCards(calculatedMaxCards);
                    }
                  }
                }}
              >
                {suggestions.slice(0, maxVisibleCards).map((suggestion) => (
                  <button
                    type="button"
                    key={suggestion.command}
                    className={cn(
                      'min-w-[140px] max-w-[180px] p-3 cursor-pointer rounded-lg',
                      'bg-black/[0.02] hover:bg-black/[0.05] border-0',
                      'flex items-center gap-3',
                    )}
                    onClick={() => handleCommandAction(suggestion)}
                  >
                    {suggestion.icon && (
                      <suggestion.icon className="w-[18px] h-[18px] text-muted-foreground" />
                    )}
                    <span className="text-sm text-foreground line-clamp-2 leading-tight">
                      {suggestion.description}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center p-6">
                <Loader className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-3 text-sm text-muted-foreground">
                  Searching...
                </span>
              </div>
            ) : (
              <div>
                {status === 'failed' && (
                  <div className="p-3 rounded-2xl bg-transparent text-destructive">
                    <p className="text-sm">
                      {error || t('assistant.search.failed')}
                    </p>
                  </div>
                )}

                {(results?.aiAnswer || isGeneratingAnswer) && (
                  <AnswerDisplay
                    aiAnswer={results?.aiAnswer || ''}
                    isGeneratingAnswer={isGeneratingAnswer}
                    lastSearchQuery={query}
                    results={{
                      data: results?.searchResults || [],
                      error: null,
                    }}
                    featuredThumbnail={
                      results?.searchResults && results.searchResults.length > 0
                        ? (() => {
                            const featuredFileId = results.featuredThumbnail;
                            const featuredFile = featuredFileId
                              ? results.searchResults.find(
                                  (f) => f._id === featuredFileId,
                                )
                              : results.searchResults[0];

                            return featuredFile
                              ? {
                                  fileId: featuredFile._id || '',
                                  fileName: featuredFile.name || '',
                                  timestamp: '0',
                                  thumbnailUrl: featuredFile.thumbUrl || null,
                                  isLoading: false,
                                }
                              : null;
                          })()
                        : null
                    }
                    activeSource={
                      activeSource as {
                        fileId: string;
                        timestamp: string;
                      } | null
                    }
                    handleSourceClick={handleSourceClick}
                    citationThumbnails={citationThumbnails}
                    fetchCitationThumbnail={fetchCitationThumbnail}
                    formatTimestamp={formatTimestamp}
                    hideReferences={hideReferences}
                    onClearHistory={handleClearHistory}
                    showVideoPlayer={showVideoPlayer}
                    videoPlayerFile={videoPlayerFile}
                  />
                )}

                {(status === 'succeeded' ||
                  (isGeneratingAnswer && results?.searchResults)) &&
                  results?.searchResults && (
                    <SearchResults
                      results={{ data: results.searchResults, error: null }}
                      isFileListExpanded={isFileListExpanded}
                      toggleFileList={toggleFileList}
                      fileThumbnails={fileThumbnails}
                      setFileThumbnails={setFileThumbnails}
                      handleClose={handleClose}
                      isLoading={false}
                      query={query}
                      aiSummary={results.aiAnswer}
                    />
                  )}
              </div>
            )}
          </div>
        </div>
      )}

      {supportDialogOpen && (
        <ContactSupportDialog
          onClose={() => setSupportDialogOpen(false)}
          formSource="Assistant - Help Command"
        />
      )}
    </div>
  );
};

export default AssistantToolbar;
